import { Scene } from 'phaser';
import { AreaRolavel } from '../components/AreaRolavel';
import { BotaoArcade } from '../components/BotaoArcade';
import { CartaoDecisao } from '../components/CartaoDecisao';
import { desenharPaginaLima } from '../components/cenario';
import { ModalArcade } from '../components/ModalArcade';
import { MolduraPixel } from '../components/MolduraPixel';
import { ICONE_DO_INDICADOR, PainelIndicadores } from '../components/PainelIndicadores';
import { criarTextoRico } from '../components/TextoRico';
import { SCENES, TEX } from '../config/constants';
import { ARCADE, ARCADE_HEX, ARCADE_TEXT } from '../config/theme';
import {
    formatarTempo, impactos, INDICADORES, indicadoresDaJornada,
    paginarAbertura, ProgressoPlayer1, resolverFaixa, separarLegenda, somar
} from '../services/player1';
import { Sessao } from '../services/Sessao';
import { t } from '../services/textos';

const CARTOES_X = [64, 354, 645, 935];
const SITUACAO = { x: 97, y: 334, largura: 748, altura: 198 };
const TEXTO_SITUACAO = { fontFamily: ARCADE_TEXT.introducao.fontFamily, fontSize: '26px', fontStyle: '500', color: ARCADE.BRANCO };

// Expressão do cliente em cada capítulo (Figma, quadros 4 a 12): é arte da
// tela, escolhida pela posição do capítulo; o conteúdo vem do catálogo.
const RETRATOS = [
    TEX.P1_CLIENTE_NEUTRO, TEX.P1_CLIENTE_CELULAR, TEX.P1_CLIENTE_DESCONFIADO,
    TEX.P1_CLIENTE_PENSATIVO, TEX.P1_CLIENTE_PREOCUPADO, TEX.P1_CLIENTE_CELULAR,
    TEX.P1_CLIENTE_PENSATIVO, TEX.P1_CLIENTE_DESCONFIADO, TEX.P1_CLIENTE_FELIZ
];

/**
 * Player 1 (Figma, quadros 1–14 e 29–32): jornada individual de capítulos,
 * cada um com uma decisão definitiva que move quatro indicadores.
 *
 * A estação roda sem rede: o catálogo chega inteiro, o tablet aplica os
 * deltas para exibir e envia a jornada uma única vez, ao final.
 */
export class Player1Scene extends Scene {
    constructor () {
        super(SCENES.PLAYER1);
    }

    async create () {
        if (!Sessao.ativa) {
            this.scene.start(SCENES.LOGIN);
            return;
        }
        this.fonte = this.registry.get('fonte');
        this.progresso = new ProgressoPlayer1(Sessao.atual.participanteId);   // RN-P1-01: individual
        this.tela = [];
        this.cartoes = [];
        this.modal = null;
        this.cronometro = null;
        this.rolagem = null;
        this.atualizarBarraDeRolagem = null;   // a instância da cena é reaproveitada entre entradas

        desenharPaginaLima(this, { titulo: t('p1.titulo'), rotuloJogador: Sessao.rotulo, rotuloY: 128, espacamento: 0 });
        this.add.image(64, 48, TEX.BTN_INSIGHTS_REDONDO).setOrigin(0)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => !this.modal && this.scene.start(SCENES.AGUARDE, {}));

        try {
            this.jornada = await this.fonte.obterCatalogoPlayer1();
        } catch (falha) {
            console.error('[Player1] não foi possível carregar os capítulos.', falha);
            if (this.scene.isActive()) {
                this.add.text(640, 400, t('p1.erro_carregar'), ARCADE_TEXT.aviso).setOrigin(0.5);
                new BotaoArcade(this, 640, 500, { rotulo: t('geral.voltar'), aoClicar: () => this.scene.start(SCENES.AGUARDE, {}) });
            }
            return;
        }
        if (!this.scene.isActive()) return;

        if (this.progresso.estado.concluido) {
            if (!Sessao.ehTeste) {
                this.scene.start(SCENES.PLAYER1_RESULTADO);
                return;
            }
            this.progresso.reiniciar();
        }
        // Avisa o painel que este participante entrou na fase: o grupo só espera por quem está jogando.
        this.fonte.registrarProgresso?.('player1', 0);
        this.progresso.iniciar();

        this.desenharBase();
        this.retomar();
    }

    update () {
        this.atualizarBarraDeRolagem?.();
    }

    get capitulos () {
        return this.jornada.capitulos;
    }

    indicadores () {
        return indicadoresDaJornada(this.capitulos, this.progresso.estado.decisoes);
    }

    /** Decide a tela a partir do progresso salvo (vale também depois de recarregar). */
    retomar () {
        const indice = this.progresso.proximoIndice(this.capitulos);
        if (indice >= this.capitulos.length) return this.abrirEncerramento();

        // RI-P1-01: o módulo abre pela instrução; o tempo só corre depois do Iniciar.
        if (!this.progresso.estado.aberturaVista) {
            this.mostrarCapitulo(indice, { comTempo: false });
            return this.abrirAbertura(0, () => {
                this.progresso.marcarAberturaVista();
                this.desenharCronometro(this.capitulo);
            });
        }
        return this.mostrarCapitulo(indice, { comTempo: true });
    }

    // ------------------------------------------------------------- base fixa

    desenharBase () {
        this.add.rectangle(64, 240, 826, 1, ARCADE_HEX.TINTA).setOrigin(0, 0.5);

        const painel = this.add.graphics();
        MolduraPixel.desenhar(painel, 59, 312, 836, 242,
            { fundo: ARCADE_HEX.CAMPO, fio: ARCADE_HEX.FIO_CAMPO, fioMeio: ARCADE_HEX.BRANCO }, { esq: true, dir: true, tamanho: 12 });

        this.retrato = this.add.image(1075, 374, RETRATOS[0]).setOrigin(0.5, 1);
        this.painel = new PainelIndicadores(this, 966, 372);
        this.painel.definir(this.indicadores());
    }

    // -------------------------------------------------------------- capítulo

    limparTela () {
        this.tela.forEach((objeto) => objeto.destroy());
        this.cartoes.forEach((cartao) => cartao.destroy());
        this.tela = [];
        this.cartoes = [];
        this.atualizarBarraDeRolagem = null;
        if (this.rolagem) {
            this.rolagem.ativa = false;
            this.rolagem.conteudo.destroy();
            this.rolagem = null;
        }
    }

    mostrarCapitulo (indice, { comTempo }) {
        this.limparTela();
        const capitulo = this.capitulos[indice];
        this.capitulo = capitulo;
        this.decidido = false;

        const rotulo = this.add.text(64, 201, t('p1.capitulo', { numero: indice + 1, total: this.capitulos.length }), ARCADE_TEXT.tituloFase).setOrigin(0, 0.5);
        const info = this.add.image(rotulo.x + rotulo.width + 12, 203, TEX.ICON_INFO_PIXEL).setOrigin(0, 0.5);
        // O "i" reabre a instrução do módulo.
        const zona = this.add.zone(64, 203, rotulo.width + 52, 48).setOrigin(0, 0.5)
            .setInteractive({ useHandCursor: true })
            .on('pointerup', () => this.abrirAbertura(0));
        const titulo = this.add.text(64, 281, capitulo.titulo.toUpperCase(), ARCADE_TEXT.manchete).setOrigin(0, 0.5);
        if (titulo.width > 880) titulo.setScale(880 / titulo.width);
        const pergunta = this.add.text(64, 604, capitulo.pergunta, { ...ARCADE_TEXT.introducao, fontSize: '26px' }).setOrigin(0, 0.5);
        this.tela.push(rotulo, info, zona, titulo, pergunta);

        this.desenharSituacao(capitulo);
        this.retrato.setTexture(RETRATOS[indice] ?? RETRATOS[0]);
        this.painel.definir(this.indicadores());

        this.cartoes = capitulo.opcoes.slice(0, 4).map((opcao, i) => {
            const cartao = new CartaoDecisao(this, CARTOES_X[i], 634, {
                texto: opcao.texto,
                // O impacto de cada alternativa só aparece depois da escolha (DP-P1-037).
                impactos: [],
                aoTocar: () => this.decidir(opcao)
            });
            cartao.opcao = opcao;
            return cartao;
        });

        if (comTempo) this.desenharCronometro(capitulo);
    }

    /** Situação do capítulo no painel escuro; textos longos rolam por arrasto (quadro 9). */
    desenharSituacao (capitulo) {
        const { x, y, largura, altura } = SITUACAO;
        this.rolagem = new AreaRolavel(this, { x, y, largura, altura, eixo: 'y' });

        let cy = 14;
        capitulo.situacao.forEach((paragrafo) => {
            // Falas entre aspas ganham destaque, como no Figma.
            const marcado = paragrafo.replace(/"([^"]+)"/g, '**"$1"**');
            const { objetos, altura: h } = criarTextoRico(this, 0, cy, largura, marcado, TEXTO_SITUACAO, { pesoDestaque: '700', entrelinha: 32 });
            this.rolagem.conteudo.add(objetos);
            cy += h + 22;
        });
        const total = cy - 22 + 14;
        this.rolagem.definirTamanho(total);
        if (total <= altura) return;

        // Sombra na base e barra à direita indicam que há mais texto.
        const sombra = this.add.graphics();
        for (let i = 0; i < 6; i++) {
            sombra.fillStyle(ARCADE_HEX.CAMPO, (i + 1) / 7).fillRect(x - 20, y + altura - 36 + i * 6, largura + 20, 6);
        }
        const barra = this.add.graphics();
        this.tela.push(sombra, barra);
        const trilho = { x: 862, y: y + 6, altura: altura - 12 };
        this.atualizarBarraDeRolagem = () => {
            const rolado = (y - this.rolagem.conteudo.y) / (total - altura);
            const cursor = Math.max(40, trilho.altura * (altura / total));
            barra.clear()
                .fillStyle(ARCADE_HEX.FIO_CAMPO, 1).fillRoundedRect(trilho.x, trilho.y, 6, trilho.altura, 3)
                .fillStyle(ARCADE_HEX.BRANCO, 1).fillRoundedRect(trilho.x, trilho.y + (trilho.altura - cursor) * rolado, 6, cursor, 3);
        };
    }

    desenharCronometro (capitulo) {
        this.pararCronometro();
        if (!capitulo.tempoSegundos) return;

        const texto = this.add.text(886, 205, '', ARCADE_TEXT.cronometro).setOrigin(1, 0.5);
        const relogio = this.add.graphics();
        const atualizar = () => {
            // O tempo é só um indicativo visual: zerado, fica em 0:00 e o jogador ainda pode responder.
            const restante = this.progresso.segundosRestantes(capitulo);
            const cor = restante <= 0 ? 0xb52a2a : ARCADE_HEX.TINTA;
            texto.setText(formatarTempo(restante)).setColor(restante <= 0 ? '#b52a2a' : ARCADE.TINTA);
            const cx = texto.x - texto.width - 22;
            relogio.clear().lineStyle(2.6, cor, 1).strokeCircle(cx, 207, 11)
                .lineBetween(cx, 200.5, cx, 208).lineBetween(cx, 208, cx + 6, 208);
        };
        const evento = this.time.addEvent({ delay: 250, loop: true, callback: atualizar });
        this.cronometro = { texto, relogio, evento };
        atualizar();
    }

    pararCronometro ({ manter = false } = {}) {
        if (!this.cronometro) return;
        this.cronometro.evento.remove();
        if (manter) {
            this.tela.push(this.cronometro.texto, this.cronometro.relogio);
        } else {
            this.cronometro.texto.destroy();
            this.cronometro.relogio.destroy();
        }
        this.cronometro = null;
    }

    // --------------------------------------------------------------- decisão

    /** RI-P1-04: registra a decisão, atualiza os indicadores e abre a consequência. */
    decidir (opcao) {
        if (this.modal || this.decidido || this.rolagem?.moveu) return;
        if (!this.progresso.decidir(this.capitulo, opcao)) return;   // RN-P1-05: definitiva
        this.fonte.registrarProgresso?.('player1', this.capitulo.ordem);   // painel do facilitador
        this.decidido = true;

        this.pararCronometro({ manter: true });
        // O cartão só marca a escolha; o impacto da decisão aparece no modal, acima do botão (DP-P1-037).
        this.cartoes.forEach((cartao) => cartao.setTocavel(false).setEstado(cartao.opcao === opcao ? 'escolhida' : 'apagada'));
        this.painel.definir(this.indicadores(), { animar: true });
        this.time.delayedCall(1100, () => this.abrirConsequencia(opcao));
    }

    abrirConsequencia (opcao) {
        const ultimo = this.progresso.proximoIndice(this.capitulos) >= this.capitulos.length;
        this.modal = new ModalArcade(this, {
            painel: { x: 237, y: 149, largura: 805, altura: 503 },
            centroY: 344, botaoY: 556, alturaMaxima: 300, largura: 620,
            blocos: [
                { imagem: TEX.P1_ICON_LAMPADA },
                { texto: t('p1.sua_acao'), peso: '700', maiusculas: true, vao: 22 },
                { texto: `“${opcao.texto.replace(/\.$/, '')}”`, peso: '600', cor: '#ffe94a', vao: 8 },
                { texto: opcao.justificativa, vao: 8 },
                // Quanto a decisão mexeu em cada indicador, logo acima do botão.
                { vao: 18, legenda: impactos(opcao.efeitos).map(({ chave, texto }) => ({ imagem: ICONE_DO_INDICADOR[chave], rotulo: texto, escala: 0.8 })) }
            ].filter((bloco) => !bloco.legenda || bloco.legenda.length > 0),
            // RI-P1-05 / RI-P1-06: só se avança; não há caminho de volta.
            botao: { rotulo: ultimo ? t('geral.continuar') : t('p1.proximo_capitulo'), aoClicar: () => this.retomar() },
            aoFechar: () => { this.modal = null; }
        });
    }

    // ---------------------------------------------------------------- modais

    /** RI-P1-02 / RI-P1-03: "toque para passar" avança a instrução; Iniciar abre o capítulo. */
    abrirAbertura (pagina, aoIniciar = null) {
        if (this.modal) return;
        const { titulo, paginas } = paginarAbertura(this.jornada.abertura);
        if (paginas.length === 0) {
            aoIniciar?.();
            return;
        }
        const ultima = pagina >= paginas.length - 1;
        const blocos = [{ texto: titulo.replace(/\.$/, ''), peso: '700', maiusculas: true }];
        paginas[pagina].forEach((paragrafo) => {
            const { texto, legenda } = separarLegenda(paragrafo);
            blocos.push({ texto, italico: /^["“]/.test(texto) });
            if (legenda) {
                blocos.push({
                    vao: 18,
                    legenda: INDICADORES.map(({ chave, rotulo }) => ({ imagem: ICONE_DO_INDICADOR[chave], rotulo }))
                });
            }
        });

        this.modal = new ModalArcade(this, {
            titulo: this.jornada.titulo,
            largura: 860,
            blocos,
            ...(ultima
                ? { centroY: 392, botaoY: 632, botao: { rotulo: t('geral.iniciar'), aoClicar: aoIniciar } }
                : {
                    centroY: 408, alturaMaxima: 400,
                    toque: { rotulo: t('p1.toque_para_passar'), aoClicar: () => this.abrirAbertura(pagina + 1, aoIniciar) }
                }),
            aoFechar: () => { this.modal = null; }
        });
    }

    /** RI-P1-07: depois da última decisão, Conferir perfil leva ao resultado. */
    abrirEncerramento () {
        if (this.modal) return;
        this.pararCronometro();
        const [manchete = '', ...demais] = this.jornada.encerramento.split(/\n\s*\n/).map((p) => p.trim()).filter(Boolean);

        this.modal = new ModalArcade(this, {
            titulo: this.jornada.titulo,
            largura: 840, centroY: 386,
            blocos: [
                { texto: manchete.replace(/\. /g, '.\n'), peso: '700', maiusculas: true },
                ...demais.map((texto) => ({ texto }))
            ],
            botao: {
                rotulo: t('p1.conferir_perfil'), largura: 420,
                aoClicar: () => {
                    // RN-P1-07: valores finais e perfil resolvidos no tablet, ainda offline.
                    const faixa = resolverFaixa(this.jornada.faixas, somar(this.indicadores()));
                    this.progresso.fechar(faixa?.titulo ?? null);
                    this.scene.start(SCENES.PLAYER1_RESULTADO);
                }
            },
            aoFechar: () => { this.modal = null; }
        });
    }
}
